import { getClientByTenantAndUserAttributes } from '.'
import { Client, UserProfile } from '@/src/interfaces'
import { setClientWithExpiry, createSession } from '@/src/utils'
import {
  updateUserAttributes,
  fetchUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth'
import crypto from 'crypto'

/**
 * Fetches user details for a given email from the API.
 * @param {string} userId - The ID of the user to fetch details for.
 * @throws {Error} If the API call fails or the response is invalid.
 * @return {Promise<any>} A Promise that resolves to the user details object.
 */
export const getUser = async (userId: string): Promise<any> => {
  try {
    const response = await fetch(`/api/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.user || {}
  } catch (error) {
    console.error('Error fetching user:', error)
    throw error
  }
}

/**
 * Fetches user role details for a given user ID from the API.
 * @param {string} userId - The ID of the user to fetch role details for.
 * @throws {Error} If the API call fails or the response is invalid.
 * @return {Promise<any>} A Promise that resolves to the user role details object.
 */
export const getRoleByUserId = async (userId: string): Promise<any> => {
  try {
    const response = await fetch(`/api/user/role`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.role || {}
  } catch (error) {
    console.error('Error fetching user role:', error)
    throw error
  }
}

/**
 * Updates user attributes with the provided new attributes.
 * @param {object} newAttributes - The new user attributes to update.
 * @throws {Error} If the update fails.
 * @returns {Promise<object>} The updated attributes.
 */
export async function handleUpdateUserAttributes(
  newAttributes: { [key: string]: string } = {}
): Promise<{ [key: string]: string }> {
  const attributesToUpdate = { ...newAttributes }

  try {
    await updateUserAttributes({
      userAttributes: attributesToUpdate,
    })
    return attributesToUpdate
  } catch (error) {
    console.error('Error updating user attributes:', error)
    throw error
  }
}

/**
 * Determines if a user is authorized to access a client.
 * @param {Record<string, string>} userAttributes - The user's attributes.
 * @return {Promise<{ authorized: boolean, client: Client | null, message: string | null }>} A Promise that resolves to an object indicating if the user is authorized and the client details.
 */
export const isUserAuthorizedForClient = async (
  userAttributes: Record<string, string>
): Promise<{
  authorized: boolean
  client: Client | null
  message: string | null
}> => {
  const { 'custom:role': userRole, 'custom:clientId': userClientId } =
    userAttributes

  const client = await getClientByTenantAndUserAttributes(userAttributes)

  const isAuthorized = userClientId === client?.id || userRole === 'admin'

  return {
    authorized: isAuthorized,
    client: client || null,
    message: isAuthorized
      ? null
      : 'This user is not authorized to login. Please contact the administrator.',
  }
}

/**
 * Starts a new session for a given user and client.
 *
 * @param {Client | null} client - The client to start the session for.
 * @throws {Error} If there is an error starting the session.
 */
export const startSessionIfAuthorized = async (client: Client | null) => {
  if (!client) {
    return
  }
  try {
    createSession('tenant', client.sub_domain || '')
    setClientWithExpiry('client', client)
  } catch (error) {
    console.error('Error starting new session:', error)
  }
}

/**
 * Generate a hash ID from an email address
 * @param email The email address to hash
 * @returns The hashed ID (hex-encoded)
 */
export function generateHashId(email: string): string {
  return crypto.createHash('sha256').update(email.toLowerCase()).digest('hex')
}

/**
 * Fetches and updates user attributes for a given email address if necessary.
 * @param {string} email - The email address of the user to fetch attributes for.
 * @throws {Error} If the fetch or update operation fails.
 * @return {Promise<Record<string, string>>} A Promise that resolves to the updated user attributes object.
 */
export const getLoggedInUserAttributes = async (
  email: string
): Promise<Record<string, string>> => {
  try {
    const currentAttributes = await fetchUserAttributes()
    const emailHash = generateHashId(email)

    const {
      'custom:userId': storedUserId,
      'custom:clientId': storedClientId,
      'custom:role': storedRole,
    } = currentAttributes

    const fetchedUser: UserProfile = await getUser(storedUserId || emailHash)
    const { id: fetchedUserId, client_id: fetchedClientId } = fetchedUser
    const { role_name: fetchedRoleName } = await getRoleByUserId(fetchedUserId)

    const updatedAttributes: Record<string, string> = {}

    if (fetchedUserId && fetchedUserId !== storedUserId) {
      updatedAttributes['custom:userId'] = fetchedUserId
    }
    if (fetchedClientId && fetchedClientId !== storedClientId) {
      updatedAttributes['custom:clientId'] = fetchedClientId
    }
    if (fetchedRoleName && fetchedRoleName !== storedRole) {
      updatedAttributes['custom:role'] = fetchedRoleName
    }

    if (Object.keys(updatedAttributes).length === 0) {
      return Object.fromEntries(
        Object.entries(currentAttributes).filter(
          ([, value]) => value !== undefined
        ) as [string, string][]
      )
    }

    return await handleUpdateUserAttributes(updatedAttributes)
  } catch (error) {
    console.error('Error fetching or updating user attributes:', error)
    throw error
  }
}

/**
 * Updates the user profile with the provided user object.
 * @param {User} user - The user object to update.
 * @returns {Promise<{ status: 'success' | 'error', message?: string | string[] }>} A Promise that resolves to an object indicating the status of the update operation.
 */
export const updateProfile = async (
  user: UserProfile
): Promise<{
  status: 'success' | 'error'
  message?: string | string[]
}> => {
  try {
    const response = await fetch('/api/user/profile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    })

    const result = await response.json()

    if (response.ok) {
      return { status: 'success', message: result.message }
    } else {
      return { status: 'error', message: result.details }
    }
  } catch (error) {
    console.error('Failed to update user profile:', error)
    throw error
  }
}

/**
 * Retrieves the user profile for the current user.
 * @returns {Promise<User>} A Promise that resolves to the user profile object.
 */
export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const { 'custom:userId': userId } = await fetchUserAttributes()

    if (!userId) {
      throw new Error('User ID not found in attributes')
    }

    return await getUser(userId)
  } catch (error) {
    console.error('Error getting user profile:', error)
    throw error
  }
}

/**
 * Retrieves the presigned S3 URL for a given file.
 * @param {File} file - The file to get the presigned URL for.
 * @throws {Error} If the presigned URL cannot be retrieved.
 * @return {Promise<string>} A Promise that resolves to the presigned S3 URL.
 */
export const getPresignedS3Url = async (file: File): Promise<string> => {
  try {
    const { signInDetails } = await getCurrentUser()

    if (!signInDetails?.loginId) {
      throw new Error('Email or user ID not found in attributes')
    }

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(
      `/api/user/profile/upload?email=${signInDetails.loginId}`,
      {
        method: 'POST',
        body: formData,
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.statusText}`)
    }

    const { objectUrl } = await response.json()

    return objectUrl
  } catch (error) {
    throw new Error(`Error getting presigned S3 URL: ${error}`)
  }
}
